/**
 * HistoryBox — style objects for the Synth VR History box (SRVR-0357). Theme tokens and accent/tint-derived
 * OKLCH only, like the Tools box above it, so the box re-tints with the accent theme.
 */
import type { CSSProperties } from 'react';
import { border, color, font, surface, ui } from '../ui/theme';

/** Height of one list row; the list is as tall as ten of them (SRVR-0357 AC4). */
export const HISTORY_ROW_HEIGHT = 22;
export const HISTORY_VISIBLE_ROWS = 10;

export const fieldStyle: CSSProperties = {
  ...ui.input, display: 'flex', alignItems: 'stretch', padding: 0, overflow: 'hidden',
  background: surface.input, fontFamily: font.sans,
};

export const fieldTextStyle: CSSProperties = {
  flex: 1, minWidth: 0, padding: '5px 8px', fontSize: 10, color: color.ink,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', userSelect: 'none',
};

export const fieldEmptyStyle: CSSProperties = { ...fieldTextStyle, color: color.inkFaint };

export function arrowBtnStyle(open: boolean, disabled: boolean): CSSProperties {
  return {
    width: 24, flexShrink: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: open ? 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.18)' : 'transparent',
    border: 'none', borderLeft: `1px solid ${border.subtle}`,
    color: disabled ? color.inkGhost : color.accentSoft,
    cursor: disabled ? 'default' : 'pointer', fontSize: 9, fontFamily: font.sans,
  };
}

export const listStyle: CSSProperties = {
  marginTop: 4, maxHeight: HISTORY_ROW_HEIGHT * HISTORY_VISIBLE_ROWS, overflowY: 'auto',
  border: `1px solid ${border.subtle}`, borderRadius: 8, background: surface.input,
  display: 'flex', flexDirection: 'column', fontFamily: font.sans,
};

// ── Hover panel ─────────────────────────────────────────────────────────────

export function panelStyle(top: number, left: number): CSSProperties {
  return {
    position: 'fixed', top, left, zIndex: 1000, minWidth: 140, maxWidth: 280,
    padding: '8px 10px', boxSizing: 'border-box', borderRadius: 10, fontFamily: font.sans,
    background: 'var(--surface-panel)', border: `1px solid ${border.accent}`,
    boxShadow: '0 8px 28px rgba(0,0,0,0.5)', pointerEvents: 'none',   // never steals the hover that opened it
  };
}

export const panelTitleStyle: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: color.ink, marginBottom: 4, paddingBottom: 4, borderBottom: `1px solid ${border.subtle}`,
};

export const panelLineStyle: CSSProperties = { fontSize: 11, lineHeight: 1.5, color: color.inkDim };

export const panelEmptyStyle: CSSProperties = { fontSize: 10, color: color.inkFaint };

export function rowStyle(newest: boolean): CSSProperties {
  return {
    height: HISTORY_ROW_HEIGHT, flexShrink: 0, boxSizing: 'border-box', padding: '0 8px',
    display: 'flex', alignItems: 'center', fontSize: 10,
    color: newest ? color.accent : color.inkDim,
    fontWeight: newest ? 600 : 400,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    borderBottom: `1px solid ${border.subtle}`,
  };
}
